import { buddyClient, qs } from './client';
import type { FeedbackStats } from './usage';

/**
 * Quality (H2): feedback triage, failed literal queries and the request audit. Types follow buddy3's
 * `packages/contracts/src/feedback.ts` and `admin.ts`, trimmed to what the hub shows. The hub always asks with
 * `scope=all`: only the platform operator reaches these pages.
 */

export type FeedbackVote = 'up' | 'down';

export interface FeedbackItem {
  id: string;
  userCode: string;
  systemId: string;
  sessionId: string;
  messageId?: string | null;
  feedback: FeedbackVote;
  comment?: string | null;
  userQuestion?: string | null;
  /** The rated answer: canonical report `{heading, answer, rows}` or an older shape (`aiResponse`). */
  report?: unknown;
  aiResponse?: unknown;
  model?: string | null;
  durationMs?: number | null;
  createdAt: string;
  handledAt?: string | null;
  handledBy?: string | null;
  handledComment?: string | null;
  showToUser?: boolean;
  userSeenAt?: string | null;
  systemName?: string | null;
  customerHandledAt?: string | null;
  customerHandledComment?: string | null;
}

export interface FeedbackListResponse {
  items: FeedbackItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface FeedbackQuery {
  page?: number;
  pageSize?: number;
  feedback?: FeedbackVote;
  /** buddy3 binds this as a boolean. */
  handled?: boolean;
}

export interface FeedbackHandledBody {
  handled: boolean;
  comment?: string | null;
  showToUser?: boolean;
}

export interface FailedQuery {
  id: string;
  systemId: string;
  systemName?: string;
  userCode?: string;
  question?: string;
  query: string;
  status: number;
  error: string;
  entity?: string;
  resolvedBy?: string | null;
  createdAt: string;
}

export interface FailedQueryQuery {
  system?: string;
  /** ISO instant. */
  since?: string;
  limit?: number;
}

export type RequestAuditKind =
  'chat' | 'replay' | 'mcp' | 'admin' | 'login' | 'attachment' | 'pdf' | 'api' | 'monitor' | (string & {});

export interface RequestAuditQuery {
  /** `Nm|Nh|Nd|all` or an ISO instant; buddy3's default `7d`. */
  period?: string;
  tenantId?: string;
  systemId?: string;
  kind?: RequestAuditKind;
  user?: string;
  limit?: number;
}

export interface RequestAuditSummary {
  kind: RequestAuditKind;
  requests: number;
  tokensIn: number;
  tokensOut: number;
  cost: number;
  averageMs: number;
}

export interface RequestAuditItem {
  id: string;
  at: string;
  tenantId?: string | null;
  systemId?: string | null;
  user?: string | null;
  kind: RequestAuditKind;
  method: string;
  path: string;
  status: number;
  durationMs: number;
  model?: string | null;
  tokensIn?: number | null;
  tokensOut?: number | null;
  cost?: number | null;
  conversationId?: string | null;
  messageId?: string | null;
  /** The first characters of the question. */
  question?: string | null;
}

export interface RequestAuditResponse {
  since?: string | null;
  scope: string;
  summary: RequestAuditSummary[];
  items: RequestAuditItem[];
}

/** The filter lists' view of buddy3's systems and tenants. */
export interface SystemRef {
  id: string;
  name: string;
  tenantId?: string;
}
export interface TenantRef {
  id: string;
  name: string;
}

export const qualityFor = (target: string) => {
  const { get, send } = buddyClient(target);
  return {
    // `qs` drops `false`, so the flag travels as text.
    feedback: (q: FeedbackQuery = {}) =>
      get<FeedbackListResponse>(
        `/feedback${qs({ scope: 'all', ...q, handled: q.handled == null ? undefined : String(q.handled) })}`
      ),
    feedbackStats: () => get<FeedbackStats>('/feedback/stats?scope=all'),
    setHandled: (id: string, body: FeedbackHandledBody) =>
      send<Partial<FeedbackItem>>('PATCH', `/feedback/${encodeURIComponent(id)}/handled`, body),
    failedQueries: (q: FailedQueryQuery = {}) => get<FailedQuery[]>(`/failed-queries${qs({ ...q })}`),
    requests: (q: RequestAuditQuery = {}) => get<RequestAuditResponse>(`/audit/requests${qs({ ...q })}`),
    systems: () => get<SystemRef[]>('/systems'),
    tenants: () => get<TenantRef[]>('/tenants'),
  };
};
